import { useState, useRef, useCallback, useEffect } from 'react';

const MAX_RECORDING_SEC = 300; // 5 minutes
const WARNING_SEC = 270; // 4:30 — 30 seconds warning

function getApiErrorMessage(error, fallbackMessage = 'Ein unbekannter Fehler ist aufgetreten') {
  const responseData = error?.response?.data;
  const directCandidates = [
    responseData?.error?.message,
    responseData?.error,
    responseData?.message,
    error?.message
  ];

  for (const candidate of directCandidates) {
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate;
    }
  }

  if (responseData?.error && typeof responseData.error === 'object') {
    try {
      return JSON.stringify(responseData.error);
    } catch (_) {
      // ignore stringify errors and use fallback below
    }
  }

  return fallbackMessage;
}

/**
 * Shared hook for voice recording with MediaRecorder API.
 * Used in ModernNotesTab and GlobalNoteDialog.
 * 
 * @param {Object} options
 * @param {Function} options.onResult - Called with { title, content, transcript, section } after processing
 * @param {Function} options.onError - Called with error message string
 * @param {Object} options.api - Axios instance for API calls
 * @returns {Object} Recording state and controls
 */
export default function useVoiceRecording({ onResult, onError, api }) {
  const [recordingState, setRecordingState] = useState('idle'); // idle | recording | processing
  const [recordingTime, setRecordingTime] = useState(0);
  const [nearLimit, setNearLimit] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const streamRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    };
  }, []);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : '';
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      mediaRecorderRef.current = recorder;
      audioChunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      recorder.start(1000);
      setRecordingState('recording');
      setRecordingTime(0);
      setNearLimit(false);

      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          const next = prev + 1;
          if (next >= WARNING_SEC && !nearLimit) {
            setNearLimit(true);
            if (navigator.vibrate) navigator.vibrate([100, 50, 100]);
          }
          if (next >= MAX_RECORDING_SEC) {
            stopRecording();
            return prev;
          }
          return next;
        });
      }, 1000);

      if (navigator.vibrate) navigator.vibrate(50);
    } catch (err) {
      console.error('Microphone access denied:', err);
      if (onError) onError('Mikrofon-Zugriff wurde verweigert. Bitte erlaube den Zugriff in den Browser-Einstellungen.');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onError]);

  const stopRecording = useCallback(async () => {
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }

    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state === 'inactive') return;

    setRecordingState('processing');

    await new Promise(resolve => {
      recorder.onstop = resolve;
      recorder.stop();
    });

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }

    if (navigator.vibrate) navigator.vibrate([50, 50, 50]);

    const audioBlob = new Blob(audioChunksRef.current, { type: recorder.mimeType || 'audio/webm' });
    audioChunksRef.current = [];

    if (audioBlob.size < 1000) {
      if (onError) onError('Aufnahme zu kurz. Bitte versuche es erneut.');
      setRecordingState('idle');
      return;
    }

    return {
      audioBlob,
      durationSec: recordingTime
    };
  }, [onError, recordingTime]);

  const processVoiceNote = useCallback(async (audioBlob, objectId, section, durationSec) => {
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'voice.webm');
      formData.append('section', section || 'allgemein');
      if (Number.isFinite(durationSec) && durationSec > 0) {
        formData.append('durationSec', String(Math.round(durationSec)));
      }

      const res = await api.post(
        `/objects/${objectId}/notes/voice`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (onResult) onResult(res.data);
    } catch (err) {
      console.error('Voice note error:', err);
      if (onError) onError(`Fehler bei der Sprachnotiz: ${getApiErrorMessage(err)}`);
    } finally {
      setRecordingState('idle');
      setRecordingTime(0);
      setNearLimit(false);
    }
  }, [api, onResult, onError]);

  const cancelRecording = useCallback(() => {
    if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    audioChunksRef.current = [];
    setRecordingState('idle');
    setRecordingTime(0);
    setNearLimit(false);
  }, []);

  const formatRecordingTime = useCallback((seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  }, []);

  return {
    recordingState,
    recordingTime,
    nearLimit,
    startRecording,
    stopRecording,
    processVoiceNote,
    cancelRecording,
    formatRecordingTime,
    MAX_RECORDING_SEC
  };
}
